/**
 * VoiceProfilesIndexView — index of voice profiles in the active workspace.
 *
 * FEAT-048 (S-285). FEAT-052 / S-299 rewrote this from "index of
 * publications' voice profiles" to "index of voice profiles" directly —
 * a profile is a shared, independent entity (multiple publications
 * already reference the same one in production), not 1:1 with a
 * publication. Route: /settings/voice; create: /settings/voice/new.
 *
 * Fetches GET /voice-profiles?agencyclient_id=X for the list, and reuses
 * the same GET /publications?agencyclient_id=X query PublicationsView
 * relies on to compute per-profile "Used by N" counts client-side in one
 * pass — not N+1 calls per profile. Clicking a row drills into
 * VoiceProfileEditor at /settings/voice/:voiceProfileId.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { voiceUsage } from "@/lib/voice-usage";
import { ApiError, useApiQuery } from "@/lib/api";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { SectionHeader } from "@/components/SectionHeader";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

interface VoiceProfile {
  id: number;
  name: string;
  createdAt?: string;
  created_at?: string;
}

interface VoiceProfilesResponse {
  data: VoiceProfile[];
  meta: { total: number; limit: number; offset: number };
}

// Minimal shape — only used here to compute per-profile usage counts.
interface Publication {
  id: number;
  voiceProfileId?: number | null;
  voice_profile_id?: number | null;
}

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

/** S-375 — only the one field this view needs off each amplifier. */
interface AmplifierLite {
  voiceProfileOverrideId?: number | null;
  voice_profile_override_id?: number | null;
}

const WS_KEY = "ccm:voice-profiles:active_ws";

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export function VoiceProfilesIndexView() {
  const navigate = useNavigate();
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (selectedId != null) {
      try {
        window.sessionStorage.setItem(WS_KEY, String(selectedId));
      } catch {
        // sessionStorage may be unavailable in some browser configs.
      }
    }
  }, [selectedId]);

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedId);
      if (!stillValid) setSelectedId(workspaces[0].id);
    }
  }, [workspaces, selectedId]);

  useEnsureActiveWorkspace(selectedId);

  const profilesQ = useApiQuery<VoiceProfilesResponse>(
    ["voice-profiles", selectedId],
    `/voice-profiles?agencyclient_id=${selectedId}`,
    { enabled: selectedId != null }
  );

  const pubsQ = useApiQuery<PublicationsResponse>(
    ["publications", selectedId],
    `/publications?agencyclient_id=${selectedId}`,
    { enabled: selectedId != null }
  );

  // S-375 — amplifiers carry their own voice override, so a profile can be in
  // use with no publication binding at all. Scoped by workspace rather than by
  // publication (the endpoint gained that scope for this) so one request answers
  // every profile, instead of one per publication.
  const ampsQ = useApiQuery<{ data: AmplifierLite[] }>(
    ["amplifiers", selectedId],
    `/amplifiers?agencyclient_id=${selectedId}`,
    { enabled: selectedId != null }
  );

  if (wsLoading) {
    return (
      <div className="settings-page">
        <div className="state-loading">Loading voice profiles…</div>
      </div>
    );
  }

  if (wsError) {
    return (
      <div className="settings-page">
        <div className="state-error">
          {wsError instanceof ApiError && wsError.status === 403
            ? "You don't have permission to view voice profiles."
            : (wsError.message ?? "Could not load workspaces.")}
        </div>
      </div>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Voice Profiles"
          title="Voice Profiles"
          description="Manage voice & tone as reusable profiles."
        />
        <div className="settings-empty">
          <h2>No workspaces yet</h2>
          <p>
            Create a workspace under Settings → Workspaces before adding
            voice profiles.
          </p>
        </div>
      </div>
    );
  }

  const profiles = profilesQ.data?.data ?? [];

  // FEAT-052 / S-299 — one pass over the already-fetched publications
  // list, not N+1 calls per profile.
  const usageCounts = new Map<number, number>();
  for (const p of pubsQ.data?.data ?? []) {
    const vid = p.voiceProfileId ?? p.voice_profile_id;
    if (vid != null) usageCounts.set(vid, (usageCounts.get(vid) ?? 0) + 1);
  }

  // S-375 — a profile can also be in use through an amplifier's voice
  // override, with no publication binding at all. The design has a case for
  // exactly that ("Used by 1 amplifier" on a profile with pubs: 0), and
  // without it such a profile reports itself unused while the pipeline is
  // actively writing to it.
  const ampCounts = new Map<number, number>();
  for (const a of ampsQ.data?.data ?? []) {
    const vid = a.voiceProfileOverrideId ?? a.voice_profile_override_id;
    if (vid != null) ampCounts.set(vid, (ampCounts.get(vid) ?? 0) + 1);
  }

  return (
    <div className="settings-page">
      <SectionHeader
        eyebrow="Settings · Voice Profiles"
        title="Voice Profiles"
        description="Manage voice & tone as reusable profiles."
      />

      <div className="team-toolbar">
        <span className="team-toolbar-label">Workspace</span>
        <select
          className="settings-select"
          value={selectedId ?? workspaces[0].id}
          onChange={(e) => setSelectedId(Number(e.target.value))}
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name} ({w.slug})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-primary"
          style={{ marginLeft: "auto" }}
          onClick={() => navigate("/settings/voice/new")}
        >
          + New voice profile
        </button>
      </div>

      {profilesQ.isLoading ? (
        <div className="state-loading">Loading voice profiles…</div>
      ) : profiles.length === 0 ? (
        <div className="settings-empty">
          <h2>No voice profiles yet</h2>
          <p>Create one to get started.</p>
        </div>
      ) : (
        <div className="idx-list">
          {profiles.map((profile) => {
            const count = usageCounts.get(profile.id) ?? 0;
            const usage = voiceUsage(count, ampCounts.get(profile.id) ?? 0);
            return (
              <Link key={profile.id} to={`/settings/voice/${profile.id}`} className="idx-row">
                <span className="idx-icon" aria-hidden>
                  ◈
                </span>
                <span className="idx-text">
                  <span className="idx-name">{profile.name}</span>
                </span>
                {/* S-375 — three states, in the prototype's own order: bound to
                    publications, else bound through an amplifier's voice
                    override, else not in use.

                    "Not in use" is deliberately QUIET. The handoff names it as
                    a neutral state that keeps a grey wash "so quiet states stay
                    quiet", and an earlier pass here made it amber by analogy to
                    the redesign's other missing-binding warnings. That analogy
                    was wrong: an unused voice profile is a fact about the
                    library, not a fault in a publication. Untested is said in
                    the editor, against scored pieces — not here. */}
                <span className="idx-meta">
                  <span className={`pill ${usage.inUse ? "pill-info" : "pill-muted"}`}>
                    {usage.label}
                  </span>
                </span>
                <span className="idx-meta">
                  {fmtDate(profile.createdAt ?? profile.created_at)}
                </span>
                <span className="idx-chevron" aria-hidden>
                  ›
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
